import { useQuery, type QueryClient } from '@tanstack/react-query';
import { api, ApiError } from './api';
import type { Me, Permission, PlanFeature } from './types';

export const ME_KEY = ['auth', 'me'] as const;

/** GET /api/auth/me, or null when signed out (401). */
export async function fetchMe(): Promise<Me | null> {
  try {
    return await api.get<Me>('/api/auth/me');
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export function loadMe(queryClient: QueryClient): Promise<Me | null> {
  return queryClient.fetchQuery({ queryKey: ME_KEY, queryFn: fetchMe, staleTime: 30_000 });
}

export function useMeQuery() {
  return useQuery({ queryKey: ME_KEY, queryFn: fetchMe, staleTime: 30_000, retry: false });
}

/** The signed-in user inside the app shell (the guard guarantees it is loaded). */
export function useMe(): Me {
  const q = useMeQuery();
  if (!q.data) throw new Error('useMe used outside the signed-in app');
  return q.data;
}

export function hasFeature(me: Pick<Me, 'entitlements'> | null | undefined, feature: PlanFeature | string): boolean {
  return !!me?.entitlements.features.includes(feature);
}

export function can(me: Pick<Me, 'permissions'> | null | undefined, permission: Permission): boolean {
  return !!me?.permissions.includes(permission);
}
